import React, { useState, useEffect, useRef } from "react";
import { Button } from "@mui/material";
import axios from "axios";
import ReactMarkdown from 'react-markdown';
import SendIcon from '@mui/icons-material/Send';
import StandardTextfield from "../standardTextfield";
import showdown from 'showdown';

export default function Step5({ onNext, chatMessages, setChatMessages, innovations }) {
  const [error, setError] = useState(null);
  const [newMessage, setNewMessage] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const [serverReplied, setServerReplied] = useState(false);
  const [buttonClicked, setButtonClicked] = useState(false);
  const [typingMessage, setTypingMessage] = useState("");
  const fullMessage = useRef("");
  const [typingHTML, setTypingHTML] = useState("");
  
  const messagesEndRef = useRef(null);
  const token = localStorage.getItem("token");

  const converter = new showdown.Converter()  

  function replace_bold_names_with_links(response) {
    const pattern = /\*\*(.*?)\*\* \(ID: (.*?)\)/g;
  
    // Replace each found bold text with an HTML link with target="_blank" if it's a startup name
    return response.replace(pattern, (match, name, sid) => {
        if (name && sid) {
            const url = `/startups/startup/${sid}`;
            return `<a href="${url}" target="_blank" rel="noopener noreferrer">${name}</a>`;
        }
        return match;  // Keep the original text if not a startup name
    });
  }

  // Funzione per inviare il messaggio con lo streaming
  const sendSelectedStartupsMessage = async () => {
    try {
        setIsTyping(true); // Show typing indicator
        setTypingHTML ("Typing..."); // Initial typing message

        const payload = { message: "Tell me more about the selected startups" };
        const response = await fetch(`${process.env.REACT_APP_API_URL}/advisory/message?stream=true`, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(payload)
        });

        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        const reader = response.body.pipeThrough(new TextDecoderStream()).getReader();
        let accumulatedText = "";

        while (true) {
            const { value, done } = await reader.read();
            if (done || accumulatedText.includes("[DONE]")) {
                break;
            }

            value.replaceAll("\n\n\n\n", '__DD__\n\n').split("\n\n").forEach((element) => {
                if (element !== "") {
                    const processedElement = element.replaceAll("__DD__", '\n\n');
                    if (processedElement.startsWith("data: ")) {
                        accumulatedText += processedElement.substring(6);
                    } else {
                        accumulatedText += processedElement;
                    }
                }
            });

            // Update the typing indicators with accumulated text
            const formattedText = converter.makeHtml(accumulatedText.replaceAll("\n   -", "\n    -"));
            setTypingHTML(formattedText); // HTML-formatted content for the typing indicator
        }

        fullMessage.current = accumulatedText
        .replace(/data:  -/g, '')    
        .replace(/data:    -/g, '')
        .replace(/data: -/g, '')
        .replace(/\[DONE\]/g, '')
        .trim();
        
        fullMessage.current = replace_bold_names_with_links(fullMessage.current);

        // Update chatMessages with the final message once fully processed
        setChatMessages(prevMessages => [
            ...prevMessages,
            { id: prevMessages.length, text: fullMessage.current, sender: "AI Navigator" },
            { id: prevMessages.length + 1, type: "options", sender: "AI Navigator" }
        ]);

        // Clear typing indicators after message is complete
        setTypingMessage("");
        setTypingHTML("");
        setIsTyping(false);
        setServerReplied(true);

    } catch (error) {
        console.error('Error during fetch:', error);
        setError('Error sending message');
        setIsTyping(false); // Ensure typing indicator is hidden in case of error
    }
  };


  useEffect(() => {
    sendSelectedStartupsMessage();
  }, []); // Call the automatic message once when the component mounts

  // Scroll to bottom when chatMessages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatMessages, typingMessage, typingHTML]);

  // Funzione per inviare nuovi messaggi
  const handleAddMessage = async () => {
    if (newMessage.trim() !== "") {
        setChatMessages(prevMessages => [
            ...prevMessages,
            { id: prevMessages.length, text: newMessage, sender: "You" }
        ]);

        setIsTyping(true);
        setTypingMessage("Typing...");

        try {
            const payload = {
                message: newMessage,
                stream: true
            };

            fullMessage.current = "";

            const response = await fetch(`${process.env.REACT_APP_API_URL}/advisory/message?stream=true`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(payload)
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            if (!response.body) {
                throw new Error('ReadableStream not supported or response body is empty.');
            }

            const reader = response.body.pipeThrough(new TextDecoderStream()).getReader();
            let accumulatedText = "";

            while (true) {
                const { value, done } = await reader.read();

                if (done || accumulatedText.includes("[DONE]")) {
                    break;
                }

                value.replaceAll("\n\n\n\n", '__DD__\n\n').split("\n\n").forEach((element) => {
                    if (element !== "") {
                        const processedElement = element.replaceAll("__DD__", '\n\n');
                        if (processedElement.startsWith("data: ")) {
                            accumulatedText += processedElement.substring(6);
                        } else {
                            accumulatedText += processedElement;
                        }
                    }
                });

                const formattedText = converter.makeHtml(accumulatedText.replaceAll("\n   -", "\n    -"));
                setTypingMessage(formattedText);
            }

            // Parsa e pulisci in caso
            fullMessage.current = accumulatedText
            .replace(/data:  -/g, '') // Remove 'data: -'
            .replace(/\[DONE\]/g, '') // Remove '[DONE]'
            .trim();

            fullMessage.current = replace_bold_names_with_links(fullMessage.current);

            // Update chat messages
            setChatMessages(prevMessages => [
            ...prevMessages,
            { id: prevMessages.length, text: fullMessage.current, sender: "AI Navigator" },
            { id: prevMessages.length + 1, type: "button", sender: "System" }
            ]);

            setTypingMessage("");
            setIsTyping(false);

        } catch (error) {
            console.error('Error during fetch:', error);
            setError('Error sending message');
            setIsTyping(false);
        }

        setNewMessage("");
    }
  };

  const handleNewMessage = (event) => {
    setNewMessage(event.target.value);
  };

  const handleGoToEvaluation = () => {
    onNext(6);
  };

  // Funzione per tornare allo Step 1
  const handleBackToStep1 = async () => {
    try {
      setIsTyping(true); 
      onNext(1);
    } catch (error) {
      window.location.reload();
      console.error('Error changing to step 1:', error);
    } finally {
      setIsTyping(false); 
    }
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault(); // Prevents adding a new line
      handleAddMessage();     // Calls your message sending function
    }
  };

  return (
    <div className="flex flex-col h-full w-full">
      {/* Chat Container */}
      <div className="flex flex-col flex-grow h-0">
        {/* Chat Messages Area */}
        <div className="flex flex-col flex-grow overflow-y-auto px-5">
        {chatMessages.map((m, index) => {
          if (m.type === 'options' && !buttonClicked && serverReplied) { // Visualizza la domanda solo se il messaggio è terminato
            return (
              <div key={`${m.sender}-${index}`} className="p-5 bg-gray-800 rounded-2xl mt-4">
                <span className="font-semibold text-white">
                  Do you want to proceed with the evaluation questionnaire or start over?
                </span>
                <div className="flex gap-2 mt-4">
                  <Button variant="contained" sx={{ backgroundColor: "rgb(var(--global-color-primary))", color: "#000" }} onClick={handleGoToEvaluation}>
                    Go to Evaluation Questionnaire
                  </Button>
                  <Button variant="outlined" sx={{ borderColor: "rgb(var(--global-color-primary))", color: "#fff" }} onClick={handleBackToStep1}>
                    Restart
                  </Button>
                </div>
              </div>
            );
          } else {
            // Render normal messages
            return (
              <div key={`${m.sender}-${index}`} className={`flex flex-col gap-1 ${m.sender === "You" ? "items-end" : "items-start"}`}>
                <span className="font-semibold">{m.sender}</span>
                <div className="p-2 bg-gray-800 w-full rounded-2xl max-w-[80%] whitespace-pre-wrap msgClass">
                  {m.sender === "AI Navigator" ? (
                    <ReactMarkdown>{m.text}</ReactMarkdown>
                  ) : (
                    <span>{m.text}</span>
                  )}
                </div>
              </div>
            );
          }
        })}

        {/* Typing Indicator */}
        {isTyping && typingHTML && (
          <div className="flex flex-col gap-1 items-start mt-4">
              <span className="font-semibold">AI Navigator</span>
              <div
                  className="p-2 bg-gray-800 rounded-2xl max-w-[80%] flex flex-col msgClass"
                  dangerouslySetInnerHTML={{ __html: typingHTML }}
              />
          </div>
        )}
        

          {/* Scroll Anchor */}
          <div ref={messagesEndRef} />
        </div>

        {/* Input Area */}
        <div className="p-5 w-full bg-black flex items-center gap-2">
          <StandardTextfield
            variant="outlined"
            multiline={true}
            value={newMessage}
            placeholder="Write here..."
            onChange={handleNewMessage}
            onKeyDown={handleKeyDown} 
            disabled={isTyping}
            style={{ flexGrow: 1 }}
          />

          <Button
            variant="contained"
            sx={{
              backgroundColor: "rgb(var(--global-color-primary))",
              color: "black",
              minWidth: '48px',
              minHeight: '48px',
              '&:hover': {
                backgroundColor: "rgb(var(--global-color-primary))",
              }
            }}
            onClick={handleAddMessage}
            disabled={isTyping}
          >
            <SendIcon />
          </Button>
        </div>
      </div>

      {/* Display error */}
      {error && <div style={{ color: "red" }}>{error}</div>}
    </div>
  );
}
